import { useRef, useState } from "react";
import { Upload, Loader2, FileDown, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import api, { formatApiError } from "../api";

const CONFIG = {
  trips: {
    label: "Trip Surat Jalan",
    cols: ["Tanggal_Muat", "No_DO", "Plat_Truk", "Nama_Supir", "Jenis_Komoditas", "PKS_Asal", "Tujuan_Bongkar", "Odometer_Awal_KM", "Odometer_Akhir_KM", "Tonase_Muat_KG", "Tonase_Bongkar_KG", "Status_Trip"],
    note: "Plat Truk dan Nama Supir harus sudah terdaftar di Master Data. Status kosong dianggap \"Selesai\" (data historis). ID Trip dibuat otomatis.",
    template: "Tanggal Muat,No DO,Plat Truk,Nama Supir,Komoditas,PKS Asal,Tujuan Bongkar,Odometer Awal,Odometer Akhir,Tonase Muat,Tonase Bongkar,Status Trip\n2026-01-15,DO-1001,BK 7745 AB,Joko Prasetyo,CPO,PKS Sei Mangkei,Refinery Belawan,120000,120350,8500,8460,Selesai\n16/01/2026,DO-1002,BK 9102 DE,Ahmad Rifai,Palm Kernel,PKS Rantau,Pelabuhan Dumai,80000,80210,7200,7150,Selesai",
  },
  kendaraan: {
    label: "Master Kendaraan",
    cols: ["Plat_Nomor", "Jenis_Truk", "Masa_Berlaku_STNK", "Masa_Berlaku_KIR", "Status_Unit"],
    template: "Plat Nomor,Jenis Truk,Masa Berlaku STNK,Masa Berlaku KIR,Status Unit\nBK 1234 XY,Tangki CPO,2027-01-31,2026-12-31,Aktif\nBK 5678 ZZ,Dump Truck,15/08/2027,20/11/2026,Aktif",
  },
  supir: {
    label: "Master Supir",
    cols: ["Nama_Supir", "NIK_KTP", "No_HP", "Status_Supir"],
    template: "Nama,NIK,No HP,Status\nBudi Contoh,1271012345678901,081234567890,Aktif\nAndi Contoh,1271098765432109,082198765432,Aktif",
  },
  gudang: {
    label: "Master Suku Cadang",
    cols: ["Kode_Barang", "Nama_Barang", "Kategori_Barang", "Harga_Beli_Satuan", "Stok_Tersedia", "Batas_Minimum_Stok"],
    template: "Kode Barang,Nama Barang,Kategori,Harga Beli,Stok,Batas Minimum\nBAN-002,Ban Luar 1000-20,Ban,2500000,10,4\nOLI-002,Oli Mesin 15W-40 Drum,Oli,3800000,3,1",
  },
};

export const downloadMasterExcel = async (entity, filename) => {
  try {
    const res = await api.get(`/export/${entity}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = filename || `${entity}.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("File Excel berhasil diunduh");
  } catch (err) {
    toast.error("Gagal mengunduh Excel");
  }
};

export const BulkImportDialog = ({ entity, open, onClose, onDone }) => {
  const cfg = CONFIG[entity];
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setPreview(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; };
  const close = () => { reset(); onClose(); };

  const downloadTemplate = () => {
    const blob = new Blob(["\ufeff" + cfg.template], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `template_${entity}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${entity}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.error) toast.error(data.error, { duration: 8000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setUploading(false);
    }
  };

  const commit = async () => {
    const validRows = preview.rows.filter((r) => r.status === "Valid");
    setCommitting(true);
    try {
      const { data } = await api.post(`/import/${entity}/commit`, {
        rows: validRows.map((r) => ({ row_no: r.row_no, data: r.data })),
      });
      setResult(data);
      toast.success(data.message);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-700" /> Impor Data Massal — {cfg.label}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4 py-2" data-testid="bulk-import-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">{result.inserted} baris berhasil diimpor</p>
                {result.rejected.length > 0 && (
                  <p className="text-sm text-red-700 mt-1">{result.rejected.length} baris ditolak: {result.rejected.map((r) => `baris ${r.row_no} (${r.alasan})`).join("; ")}</p>
                )}
              </div>
            </div>
            <Button data-testid="bulk-close-button" onClick={close} className="bg-emerald-700 hover:bg-emerald-800 text-white">Selesai</Button>
          </div>
        ) : !preview ? (
          <div className="space-y-5 py-2">
            <p className="text-sm text-zinc-600">
              Unggah file <b>CSV atau Excel (.xlsx)</b>. Sistem akan <b>memetakan kolom secara otomatis</b> (mis. kolom "Nama" → Nama_Supir) dan menandai baris <b>duplikat</b> agar tidak masuk dua kali.
            </p>
            {cfg.note && (
              <p data-testid="bulk-entity-note" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">{cfg.note}</p>
            )}
            <div className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center hover:border-emerald-400 transition-colors duration-200">
              <input ref={fileRef} data-testid="bulk-file-input" type="file" accept=".csv,.xlsx" onChange={handleFile}
                className="block w-full text-sm text-zinc-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-emerald-700 file:text-white file:text-sm file:font-medium hover:file:bg-emerald-800 file:cursor-pointer file:transition-colors" />
              {uploading && <p className="text-sm text-zinc-500 mt-3 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Membaca &amp; memetakan kolom...</p>}
            </div>
            <button data-testid="bulk-download-template" onClick={downloadTemplate}
              className="text-sm text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1.5 transition-colors duration-200">
              <FileDown className="w-4 h-4" /> Unduh Template CSV ({cfg.label})
            </button>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Pemetaan Kolom Otomatis</p>
              <div className="flex flex-wrap gap-2" data-testid="bulk-mapping">
                {cfg.cols.map((f) => (
                  <span key={f} className={`text-xs rounded-md px-2 py-1 border font-medium ${preview.mapping[f] ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-zinc-100 text-zinc-400 border-zinc-200"}`}>
                    {preview.mapping[f] ? `"${preview.mapping[f]}" → ${f}` : `${f}: tidak ditemukan`}
                  </span>
                ))}
              </div>
            </div>

            {preview.error ? (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3" data-testid="bulk-mapping-error">{preview.error}</p>
            ) : (
              <>
                <div className="flex items-center gap-3 text-sm">
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="bulk-valid-count">{preview.valid_count} Valid</Badge>
                  <Badge className="bg-red-100 text-red-800 border border-red-200" data-testid="bulk-rejected-count">{preview.rejected_count} Ditolak</Badge>
                  <span className="text-zinc-500">dari {preview.total_rows} baris data</span>
                </div>
                <div className="border border-zinc-200 rounded-md overflow-auto max-h-72">
                  <table className="w-full text-xs" data-testid="bulk-preview-table">
                    <thead className="sticky top-0 bg-zinc-50">
                      <tr className="border-b border-zinc-200">
                        <th className="px-3 py-2 text-left font-medium text-zinc-500">Baris</th>
                        {cfg.cols.map((c) => <th key={c} className="px-3 py-2 text-left font-medium text-zinc-500 whitespace-nowrap">{c}</th>)}
                        <th className="px-3 py-2 text-left font-medium text-zinc-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r) => (
                        <tr key={r.row_no} className={`border-b border-zinc-100 ${r.status === "Ditolak" ? "bg-red-50/60" : ""}`} data-testid={`bulk-row-${r.row_no}`}>
                          <td className="px-3 py-2 text-zinc-400">{r.row_no}</td>
                          {cfg.cols.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap">{String(r.data[c] ?? "") || "-"}</td>)}
                          <td className="px-3 py-2">
                            {r.status === "Valid" ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Valid</span>
                            ) : (
                              <span className="inline-flex items-start gap-1 text-red-700 font-medium" title={r.alasan}><XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {r.alasan}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="flex items-center gap-2">
              <Button data-testid="bulk-back-button" variant="outline" onClick={reset} className="border-zinc-300">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Pilih File Lain
              </Button>
              {!preview.error && (
                <Button data-testid="bulk-commit-button" onClick={commit} disabled={committing || preview.valid_count === 0}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  {committing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  Impor {preview.valid_count} Baris Valid
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
